import React, { useState } from 'react';
import classnames from 'classnames';
import * as styles from '../OpportunityReviewBoard.module.scss';
import { GenericCell } from '@components/design/migrated/Table';
import { ColumnHeaderForOR, GroupState, OpportunityReviewTableContent } from '@models/OpportunityReview';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import {
  displayPrimaryCell,
  displayPrimaryCellSingleValue,
  displaySecColorForPositiveGrowth,
  displaySecondaryCell,
} from '@utils/hook/tableCellConst/TableCellConst';
import { getGrandTotalValue, modifiedORTableContentGrandTotalToTotalValue } from '@utils/TotalUtils';
import { useOpportunityReviewBoardGroupEventState } from '@components/opportunity-review-group/utils/OpportunityReviewBoardGroupContext';
import { elements, feedback, inputs } from '@ecosystem/newron-design-system';
import { cloneDeep, noop } from 'lodash';
import OpportunityReviewGroupTableCell from '@components/opportunity-review-group/OpportunityReviewGroupTableCell';
import {
  constructProductGroupFilter,
  getBaseFilterOptionsFromResponseConfig,
  updateAppliedFilters,
} from '@utils/FilterUtils';
import { opportunityReviewResponseConfig } from '@utils/FilterSettings';
import { DataType, FilterKeys, Filters, PageType, SelectedFilters } from '@utils/pageConfig/Types';
import {
  PRODUCT_GROUP,
  TABLE_CLICKED_TRACKING_TITLE,
  TABLE_PRODUCT_GROUP,
  tableClickTrackingFunction,
  TableClickTrackingParam,
} from '@utils/page-analytics/model/TableClickTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { bindActionCreators } from 'redux';
import { actionCreatorsSideFilters } from '@state/Index';

interface Props {
  headerKey: string;
  opportunityReviewTableContent: OpportunityReviewTableContent;
  isStickyRow?: boolean;
  isCategoryGroup: boolean;
  sortedColumn: string;
  selectedGrowthType: GrowthTypes;
  currencySymbol: string;
  isGroupingHeader?: boolean;
  isRowHovered?: boolean;
  groupState?: GroupState;
  isPreferenceOwner?: boolean;
}

const OpportunityReviewTableCell: React.FC<Props> = ({
  headerKey,
  opportunityReviewTableContent,
  isStickyRow,
  sortedColumn,
  isCategoryGroup,
  selectedGrowthType,
  currencySymbol,
  isRowHovered,
  groupState,
  isPreferenceOwner,
}) => {
  const { Tooltip } = feedback;
  const [toggleGroupDown, setToggleGroupDown] = useState<boolean>(false);
  const {
    isSegmentsSelected,
    setIsDeleteModalGroupPopUp,
    setGroupNameToBeDelete,
    isEditGroupMode,
    setIsShareModalGroupPopUp,
    clearSelectedSegments,
  } = useOpportunityReviewBoardGroupEventState();

  const pagesConfig = usePageConfig();
  const { track } = useAnalytics();

  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const { feature } = useSelector((state: IRootState) => state.availabilityPg);

  const dispatch = useDispatch();
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);

  const modifiedOpportunityReviewTableContent =
    modifiedORTableContentGrandTotalToTotalValue(opportunityReviewTableContent);

  //Handle Group dropdown state
  const handleGroupDropdownToggle = () => {
    const prevState = toggleGroupDown;

    setToggleGroupDown(!prevState);
    groupState?.setIsGroupingRowExpanded?.(!prevState);
  };

  //Handle no checkbox displayed state
  const noCheckbox = (): boolean => {
    return (
      groupState?.isGroupingProductGroup ??
      groupState?.isGroupingHeader ??
      opportunityReviewTableContent.segment === getGrandTotalValue()
    );
  };

  //Check if checkbox should be checked
  const isCheckboxCheck = (): boolean => {
    if (
      isEditGroupMode.isEditMode &&
      isEditGroupMode.groupDetails.groupElement.includes(modifiedOpportunityReviewTableContent.segment)
    ) {
      return true;
    }

    return isSegmentsSelected(modifiedOpportunityReviewTableContent.segment);
  };

  //Check if checkbox should be disabled
  const isCheckboxDisable = (): boolean => {
    return (
      isEditGroupMode.isEditMode &&
      isEditGroupMode.groupDetails.groupElement.includes(modifiedOpportunityReviewTableContent.segment)
    );
  };

  //Handle to open of the delete modal group state
  const openDeleteModalGroup = () => {
    setGroupNameToBeDelete(groupState?.groupName);
    setIsDeleteModalGroupPopUp(true);
  };
  const openShareModalGroup = () => {
    setGroupNameToBeDelete(groupState?.groupName);
    setIsShareModalGroupPopUp(true);
  };

  //To display checkbox
  if (ColumnHeaderForOR.checkbox === headerKey) {
    return (
      <td
        className={classnames([styles.OpportunityReviewBoard__TableCell], {
          [styles['OpportunityReviewBoard__StickyRow']]: isStickyRow,
        })}
      >
        {!noCheckbox() && (
          <inputs.Checkbox
            disabled={isCheckboxDisable()}
            state={isCheckboxCheck() ? inputs.CheckboxState.CHECKED : inputs.CheckboxState.UNCHECKED}
            onChange={noop}
            onClick={noop}
          ></inputs.Checkbox>
        )}
      </td>
    );
  }

  const renderGroupCell = () => {
    if (groupState?.isGroupingHeader) {
      return (
        <OpportunityReviewGroupTableCell
          groupState={groupState}
          isRowHovered={isRowHovered}
          toggleGroupDown={toggleGroupDown}
          handleGroupDropdownToggle={handleGroupDropdownToggle}
          openDeleteModalGroup={openDeleteModalGroup}
          segment={modifiedOpportunityReviewTableContent.segment}
          isEditGroupMode={isEditGroupMode}
          openShareModalGroup={openShareModalGroup}
          isPreferenceOwner={isPreferenceOwner}
        />
      );
    }
    if (!groupState?.isGroupingHeader) {
      return (
        <td
          key={modifiedOpportunityReviewTableContent.segment}
          className={classnames([
            styles.OpportunityReviewBoard__TableCell,
            styles.OpportunityReviewBoard__TableCell_Total,
          ])}
        >
          {isStickyRow ? (
            <div>
              <p>{modifiedOpportunityReviewTableContent.segment}</p>
            </div>
          ) : (
            <div
              className={classnames([
                styles.OpportunityReviewBoard__TableCell__grouping,
                styles.OpportunityReviewBoard__TableCell__grouping__pgsWithDeleteButton,
              ])}
            >
              {renderSegmentCell()}
              {groupState?.isGroupingProductGroup && isRowHovered && (
                <Tooltip placement="top">
                  <Tooltip.Content>
                    <p>Remove product group from group</p>
                  </Tooltip.Content>
                  {isPreferenceOwner && (
                    <button
                      type="button"
                      aria-label="Remove product group from group"
                      onClick={() => groupState?.onDeleteGroupPgs?.(modifiedOpportunityReviewTableContent.segment)}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="icon-button brand"
                    >
                      <elements.Icon color="brand">
                        <elements.Icon.icons.RemoveOutline />
                      </elements.Icon>
                    </button>
                  )}
                </Tooltip>
              )}
            </div>
          )}
        </td>
      );
    }
  };

  const applySelectedPGFilter = (event) => {
    event.stopPropagation();
    // clear the previous selection before applying filter
    clearSelectedSegments();

    const { segment } = modifiedOpportunityReviewTableContent;

    const productGroupOptions = getBaseFilterOptionsFromResponseConfig(
      opportunityReviewResponseConfig,
      FilterKeys.PRODUCT_GROUP,
    );

    const originalProductGroupOptions = productGroupOptions.find(
      ({ label }) => label === segment, // This comparison is case sensitive
    ) ?? {
      value: segment,
      label: segment,
    };
    const productGroupToUpdate: Filters = constructProductGroupFilter([originalProductGroupOptions]);

    const allFilters: SelectedFilters[] = cloneDeep(pagesConfig.appliedFilters);

    const appliedORFilters = allFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters;
    const appliedExploreFilters = allFilters[PageType.EXPLORE].selectedFilters;
    const appliedROFilters = allFilters[PageType.RETAILER_OVERVIEW].selectedFilters;

    updateAppliedFilters(productGroupToUpdate, appliedORFilters);
    updateAppliedFilters(productGroupToUpdate, appliedExploreFilters);
    updateAppliedFilters(productGroupToUpdate, appliedROFilters);

    pagesConfig.setSelectedFilters(allFilters);
    pagesConfig.setAppliedFilters(allFilters);
    updateSideFilters({
      appliedFilters: allFilters,
    });

    //Starts table click amplitude tracking
    const trackingParam: TableClickTrackingParam = {
      eventTitle: TABLE_CLICKED_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      track: track,
      componentName: PRODUCT_GROUP,
      elementType: TABLE_PRODUCT_GROUP,
      retailerClientState: retailerClientState,
      pageType: PageType.CATEGORY_LEADERBOARD,
      pageName: retailerClientState.pageViewing,
      optionChosen: modifiedOpportunityReviewTableContent.segment,
      access: pagesConfig.access,
    };

    tableClickTrackingFunction(trackingParam);
    //Ends table click amplitude tracking
  };

  const renderSegmentCell = () => {
    if (feature?.dataType === DataType.CATEGORY) {
      return (
        <button
          tabIndex={0}
          className={styles.OpportunityReviewBoard__TableCell_CategoryGroup_title}
          onClick={applySelectedPGFilter}
          onKeyDown={applySelectedPGFilter}
        >
          <p>{modifiedOpportunityReviewTableContent.segment}</p>
        </button>
      );
    }
    return (
      <div>
        <p>{modifiedOpportunityReviewTableContent.segment}</p>
      </div>
    );
  };

  if (isCategoryGroup) {
    if (groupState) {
      return renderGroupCell();
    }

    return (
      <td
        key={modifiedOpportunityReviewTableContent.segment}
        className={classnames([
          styles.OpportunityReviewBoard__TableCell,
          styles.OpportunityReviewBoard__TableCell_Total,
          { [styles['OpportunityReviewBoard__StickyRow']]: isStickyRow },
        ])}
      >
        {isStickyRow ? (
          <div>
            <p>{modifiedOpportunityReviewTableContent.segment}</p>
          </div>
        ) : (
          renderSegmentCell()
        )}
      </td>
    );
  }
  const oppSingleValueKpis = [
    'fairSharePerformanceRevenue',
    'fairSharePerformanceVolume',
    'growthPerformanceRevenue',
    'growthPerformanceVolume',
    'growthOppRevenue',
    'growthOppVolume',
    'fairShareOppRevenue',
    'fairShareOppVolume',
    'swotCategoryRevenue',
    'swotCategoryVolume',
  ];
  const displayCellWithValue = () => {
    return (
      <>
        {oppSingleValueKpis.includes(headerKey) ? (
          <GenericCell
            primary={displayPrimaryCellSingleValue(headerKey, modifiedOpportunityReviewTableContent, currencySymbol)}
          />
        ) : (
          <GenericCell
            primary={displayPrimaryCell(headerKey, modifiedOpportunityReviewTableContent, currencySymbol, false)}
            secondary={displaySecondaryCell(
              headerKey,
              modifiedOpportunityReviewTableContent,
              selectedGrowthType,
              currencySymbol,
              false,
            )}
            secondaryColor={displaySecColorForPositiveGrowth(
              headerKey,
              modifiedOpportunityReviewTableContent,
              selectedGrowthType,
              false,
            )}
          />
        )}
      </>
    );
  };

  return (
    <td
      className={classnames([
        styles.OpportunityReviewBoard__TableCell,
        styles.OpportunityReviewBoard__TableCell_Kpi,
        {
          [styles['OpportunityReviewBoard__SortedCell']]: sortedColumn === headerKey,
          [styles['OpportunityReviewBoard__StickyRow']]: isStickyRow,
        },
      ])}
    >
      {groupState?.isGroupEmpty ? <GenericCell primary={'-'} /> : displayCellWithValue()}
    </td>
  );
};

export default OpportunityReviewTableCell;
